import { useState } from 'react';
import { HelperList } from '@/components/helpers/helper-list';
import { TaskAssignmentDialog, type TaskAssignmentData } from '@/components/helpers/task-assignment-dialog';
import { useToast } from '@/hooks/use-toast';
import type { FlexHelper } from '@/lib/types';

// Mock data with extended information
const mockHelpers: FlexHelper[] = [
  {
    id: '1',
    name: 'John Smith',
    status: 'available',
    currentTask: undefined,
    currentLocation: {
      lat: 40.7128,
      lng: -74.0060,
      accuracy: 10,
      timestamp: new Date().toISOString(),
    },
  },
  {
    id: '2',
    name: 'Sarah Johnson',
    status: 'on_task',
    currentTask: 'HVAC maintenance at Maple Court',
    currentLocation: {
      lat: 40.7580,
      lng: -73.9855,
      accuracy: 15,
      timestamp: new Date().toISOString(),
    },
  },
  {
    id: '3',
    name: 'Michael Brown',
    status: 'available',
    currentTask: undefined,
    currentLocation: {
      lat: 40.7829,
      lng: -73.9654,
      accuracy: 12,
      timestamp: new Date().toISOString(),
    },
  },
];

export function HelpersPage() {
  const [search, setSearch] = useState('');
  const [selectedHelper, setSelectedHelper] = useState<FlexHelper | null>(null);
  const { toast } = useToast();

  const filteredHelpers = mockHelpers.filter(helper =>
    helper.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAssignTask = (helperId: string) => {
    const helper = mockHelpers.find(h => h.id === helperId);
    if (helper?.status === 'on_task') {
      toast({
        title: 'Helper Unavailable',
        description: 'This helper is currently assigned to another task.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedHelper(helper || null);
  };

  const handleTaskAssignment = (data: TaskAssignmentData) => {
    if (!selectedHelper) return;
    
    // Here you would make an API call to assign the task
    console.log('Assigning task:', { helperId: selectedHelper.id, ...data });
    
    toast({
      title: 'Task Assigned',
      description: `Task "${data.title}" has been assigned to ${selectedHelper.name}`,
    });
    
    setSelectedHelper(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">FlexHelpers</h1>
      </div>

      <HelperList
        helpers={filteredHelpers}
        searchQuery={search}
        onSearchChange={setSearch}
        onAssign={handleAssignTask}
      />

      {selectedHelper && (
        <TaskAssignmentDialog
          helperName={selectedHelper.name}
          open={true}
          onClose={() => setSelectedHelper(null)}
          onAssign={handleTaskAssignment}
        />
      )}
    </div>
  );
}